import type { Prompt, PromptFolder } from ".";

export type MainframePrompts =
	| AgenticCreateArtifactPrompt
	| ChatNoSourcePrompt
	| CleanArtifactPrompt
	| CodeBasedTableAnalysisPrompt
	| CodeBasedTableAnalysisSheetSelectionPrompt
	| ConciseReformulationPrompt
	| UpdateConversationStatePrompt
	| DeepDiffPrompt
	| EditArtifactPrompt
	| LanguageDetectionPrompt
	| LinkupQueryGenerationPrompt
	| MainFramePrompt
	| NeedsUpdateAgentPrompt;

/* eslint-disable @typescript-eslint/naming-convention */
export const mainframePromptFolders = {
	"needs-update-agent": "mainframe",
	"agentic-create-artifact": "mainframe",
	"chat-no-source": "mainframe",
	"clean-artifact-agent": "mainframe",
	"code-based-table-analysis": "mainframe",
	"code-based-table-analysis-sheet-selection": "mainframe",
	"concise-reformulation": "mainframe",
	"conversation-state": "mainframe",
	"deep-diff": "mainframe",
	"edit-artifact-agent": "mainframe",
	"language-detection": "mainframe",
	"linkup-query-generation": "mainframe",
	mainframe: "mainframe",
} as const satisfies Record<MainframePrompts["name"], PromptFolder>;
/* eslint-enable @typescript-eslint/naming-convention */

export interface AgenticCreateArtifactPrompt extends Prompt {
	name: "agentic-create-artifact";
	variables: {
		query: string;
		documents: string;
		linkupResults: string;
		language: string;
		date: string;
		userPreferences: string;
		companyPreferences: string;
		userMemories: string;
		conversationState: string;
		chatHistory: string;
		brainsContext: string;
		artifactContent: string;
	};
}

export interface ChatNoSourcePrompt extends Prompt {
	name: "chat-no-source";
	variables: {
		query: string;
		language: string;
		date: string;
		conversationState: string;
		artifactContent: string;
		chatHistory: string;
		brainsContext: string;
		companyPreferences: string;
		userPreferences: string;
		userMemories: string;
	};
}

export interface CodeBasedTableAnalysisPrompt extends Prompt {
	name: "code-based-table-analysis";
	variables: {
		query: string;
		documentTitle: string;
		documentSummary: string;
		filePath: string;
		fileStructure: string;
		mimeType: string;
		language: string;
	};
}

export interface CodeBasedTableAnalysisSheetSelectionPrompt extends Prompt {
	name: "code-based-table-analysis-sheet-selection";
	variables: {
		query: string;
		sheets: string;
		language: string;
	};
}

export interface UpdateConversationStatePrompt extends Prompt {
	name: "conversation-state";
	variables: {
		conversationState: string;
		chatHistory: string;
		artifactContent: string;
	};
}

export interface DeepDiffPrompt extends Prompt {
	name: "deep-diff";
	variables: {
		changes: string;
		sourcePages: string;
		targetPage: string;
	};
}

export interface EditArtifactPrompt extends Prompt {
	name: "edit-artifact-agent";
	variables: {
		query: string;
		language: string;
		documents: string;
		companyPreferences: string;
		userMemories: string;
		userPreferences: string;
		date: string;
		artifact: string;
		artifactStructure: string;
	};
}

export interface CleanArtifactPrompt extends Omit<EditArtifactPrompt, "name"> {
	name: "clean-artifact-agent";
}

export interface LanguageDetectionPrompt extends Prompt {
	name: "language-detection";
	variables: {
		query: string;
		date: string;
		chatHistory: string;
	};
}

export interface NeedsUpdateAgentPrompt extends Prompt {
	name: "needs-update-agent";
	variables: {
		query: string;
		artifact: string;
		context: string;
	};
}

export interface MainFramePrompt extends Prompt {
	name: "mainframe";
	variables: {
		query: string;
		language: string;
		date: string;
		userPreferences: string;
		companyPreferences: string;
		conversationState: string;
		chatHistory: string;
		brainsContext: string;
		artifactContent: string;
	};
}

export interface LinkupQueryGenerationPrompt extends Prompt {
	name: "linkup-query-generation";
	variables: {
		query: string;
		language: string;
		date: string;
		conversationState: string;
		chatHistory: string;
	};
}

export interface ConciseReformulationPrompt extends Prompt {
	name: "concise-reformulation";
	variables: {
		language: string;
		query: string;
		conversationState: string;
		chatHistory: string;
		date: string;
	};
}
